/**
 * NavbarMobileDrawer — the hamburger drawer rendered below the navbar
 * on ≤768px viewports. Mirrors the desktop grouping (Learn / Watch /
 * Tools) so users get the same mental model regardless of screen size.
 *
 * Extracted from `Navbar.jsx` to keep the shell small.
 */
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NAV_GROUPS } from "@/components/nav/navGroups";

const mobileLinkClass = ({ isActive }) =>
  `block w-full rounded-md px-3 py-2 text-sm transition-colors ${
    isActive ? "bg-rp-surface2 text-rp-text" : "text-rp-textDim hover:bg-rp-surface2 hover:text-rp-text"
  }`;

export default function NavbarMobileDrawer({
  user, order, hidden, renderTopNavItem, onClose,
}) {
  const hiddenSet = new Set(hidden);
  return (
    <div className="md:hidden border-t border-rp-border bg-rp-bg" data-testid="nav-mobile-drawer">
      <nav className="rp-container py-3 flex flex-col gap-1">
        {order
          .filter((id) => !hiddenSet.has(id))
          .map((id) => (
            <span key={id}>{renderTopNavItem(id, mobileLinkClass)}</span>
          ))}
        {user && (
          <NavLink
            to="/dashboard"
            className={mobileLinkClass}
            data-testid="nav-mobile-dashboard"
            onClick={onClose}
          >
            Dashboard
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink
            to="/admin"
            className={mobileLinkClass}
            data-testid="nav-mobile-admin"
            onClick={onClose}
          >
            Admin
          </NavLink>
        )}
        {NAV_GROUPS.map((g) => (
          <div key={g.id} className="pt-3" data-testid={`nav-mobile-group-${g.testId}`}>
            <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-rp-textFaint">
              {g.label}
            </div>
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={mobileLinkClass}
                onClick={onClose}
                data-testid={`nav-mobile-${it.testId.replace(/^nav-group-/, "")}`}
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        ))}
        <NavLink
          to="/help"
          className={mobileLinkClass}
          data-testid="nav-mobile-help"
          onClick={onClose}
        >
          Help
        </NavLink>
        {!user && (
          <div className="flex gap-2 pt-2">
            <Link to="/login" className="flex-1" onClick={onClose} data-testid="nav-mobile-login">
              <Button variant="ghost" className="w-full text-rp-textDim hover:text-rp-text hover:bg-rp-surface2">Log in</Button>
            </Link>
            <Link to="/signup" className="flex-1" onClick={onClose} data-testid="nav-mobile-signup">
              <Button className="w-full bg-rp-accent hover:bg-rp-accentHover text-white">Get started</Button>
            </Link>
          </div>
        )}
      </nav>
    </div>
  );
}
